import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Trash, X } from 'lucide-react';
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import { BadgeAlt, Button, DataTable, Label, TablePagination, } from '@cargomatic/ui';
import CustomerSearch from 'components/site/customer-search';
import { ConfirmationModal } from 'components/v2/confirmation-modal';
import CustomersAPIUtils from 'utils/CustomersAPIUtils';
import { getTariffsUsage } from 'utils/TariffAPIUtils';
import CustomersStore from 'stores/CustomerDetailsStore';
import ToastStore from 'stores/ToastStore';
function showToast(title, body) {
    ToastStore.getNotifications().push({ title, body, level: 'error' });
    ToastStore.emitChange();
}
const columnHelper = createColumnHelper();
export function TariffShippersTable({ tariff }) {
    const { t } = useTranslation();
    const [shippers, setShippers] = useState([]);
    const [selectedShipper, setSelectedShipper] = useState(null);
    const [pendingRemoveShipper, setPendingRemoveShipper] = useState(null);
    const [confirmingAssign, setConfirmingAssign] = useState(false);
    // Subscribe to CustomersStore — refetches the assigned-shippers list when a
    // mutation (assign/unassign) succeeds.
    useEffect(() => {
        const onChange = () => {
            if (CustomersStore.getAssignedShipperId()) {
                getTariffsUsage();
                CustomersStore.reset();
                if (tariff._id) {
                    CustomersAPIUtils.getShippersWithTariff(tariff._id);
                }
            }
            else {
                setShippers(CustomersStore.getShippers() || []);
                const next = CustomersStore.getShipper();
                if (next === null || next === void 0 ? void 0 : next.tariffs) {
                    next.tariffs = next.tariffs.filter((tt) => tt != null);
                }
                setSelectedShipper(next || null);
            }
        };
        CustomersStore.addChangeListener(onChange);
        CustomersStore.reset();
        CustomersStore.clearShippers();
        if (tariff._id) {
            CustomersAPIUtils.getShippersWithTariff(tariff._id);
        }
        return () => CustomersStore.removeChangeListener(onChange);
    }, [tariff._id]);
    const handleShipperPicked = useCallback(
    // CustomerSearch passes (displayName, item) where item carries `.value` =
    // the customer _id (set by CustomerSearchStore). The first arg is the
    // titleized name, not an ID, so we route by `item.value`.
    (_displayName, item) => {
        const id = (item === null || item === void 0 ? void 0 : item.value) || (item === null || item === void 0 ? void 0 : item._id);
        if (id) {
            CustomersAPIUtils.getShipper(id);
        }
        else {
            setSelectedShipper(null);
        }
    }, []);
    const handleAssignConfirm = useCallback(() => {
        setConfirmingAssign(false);
        if (!selectedShipper || !tariff)
            return;
        const alreadyAssigned = (selectedShipper.tariffs || []).some((entry) => {
            const entryId = typeof entry === 'string' ? entry : entry === null || entry === void 0 ? void 0 : entry._id;
            return entryId === tariff._id;
        });
        if (alreadyAssigned) {
            showToast('Error', t('SHIPPER_ALREADY_HAS_TARIFF'));
            return;
        }
        CustomersAPIUtils.assignTariff(selectedShipper, tariff, '');
    }, [selectedShipper, tariff, t]);
    const handleRemoveConfirm = useCallback(() => {
        if (!pendingRemoveShipper)
            return;
        const target = Object.assign({}, pendingRemoveShipper);
        if (Array.isArray(target.tariffs)) {
            target.tariffs = target.tariffs.filter((item) => {
                if (typeof item === 'string')
                    return item !== tariff._id;
                return (item === null || item === void 0 ? void 0 : item._id) !== tariff._id;
            });
        }
        CustomersAPIUtils.unassignTariff(target, tariff, '');
        setPendingRemoveShipper(null);
    }, [pendingRemoveShipper, tariff]);
    const columns = useMemo(() => [
        columnHelper.accessor('company_name', { header: t('COMPANY_NAME') }),
        columnHelper.accessor('email', { header: t('EMAIL') }),
        columnHelper.accessor('phone', { header: t('PHONE') }),
        columnHelper.display({
            id: 'actions',
            header: () => null,
            cell: ({ row }) => (React.createElement("div", { className: "text-right" },
                React.createElement(Button, { variant: "ghost", size: "sm", onClick: () => setPendingRemoveShipper(row.original), "aria-label": titleize(t('REMOVE')), title: titleize(t('REMOVE')) },
                    React.createElement(Trash, { className: "h-4 w-4 text-destructive" })))),
            enableSorting: false,
            size: 60,
        }),
    ], [t]);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const table = useReactTable({
        columns,
        data: shippers,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: { sorting, pagination },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
    });
    return (React.createElement("div", null,
        React.createElement("div", { className: "flex items-end gap-3 mb-4" },
            React.createElement("div", { className: "flex-1 min-w-0" },
                React.createElement(Label, { className: "mb-1.5 block" }, t('ASSIGN_TO_SHIPPER')),
                selectedShipper ? (React.createElement("div", { className: "flex h-10 items-center px-3 rounded-md border border-input bg-background" },
                    React.createElement(BadgeAlt, { variant: "secondary", className: "gap-1 pr-1" },
                        React.createElement("span", null, selectedShipper.company_name ||
                            selectedShipper.email ||
                            selectedShipper._id),
                        React.createElement("button", { type: "button", onClick: () => setSelectedShipper(null), className: "rounded-sm p-0.5 hover:bg-muted-foreground/20", "aria-label": titleize(t('REMOVE')) },
                            React.createElement(X, { className: "h-3 w-3" }))))) : (React.createElement(CustomerSearch, { type: "shippers", onSelect: handleShipperPicked, placeholder: t('SEARCH_FOR_SHIPPER_PLACEHOLDER') }))),
            React.createElement(Button, { onClick: () => setConfirmingAssign(true), disabled: !selectedShipper }, t('ASSIGN'))),
        React.createElement(DataTable, { table: table }),
        shippers.length > 0 && (React.createElement(TablePagination, { page: pagination.pageIndex + 1, pages: table.getPageCount(), total: shippers.length, perPage: pagination.pageSize, onPageChange: (p) => table.setPageIndex(p - 1), pageSizeOptions: [10, 20, 50], onPageSizeChange: (size) => table.setPageSize(size) })),
        React.createElement(ConfirmationModal, { open: confirmingAssign, title: t('WARNING'), description: t('IMPACT_CUSTOMERS_MSG'), onHide: () => setConfirmingAssign(false), onConfirm: handleAssignConfirm, confirmText: t('CONFIRM_ASSIGN'), cancelText: titleize(t('CANCEL')) }),
        React.createElement(ConfirmationModal, { open: !!pendingRemoveShipper, title: t('WARNING'), description: t('IMPACT_CUSTOMERS_MSG'), onHide: () => setPendingRemoveShipper(null), onConfirm: handleRemoveConfirm, confirmText: t('CONFIRM_UNASSIGN'), cancelText: titleize(t('CANCEL')) })));
}
export default TariffShippersTable;
